/**
 * TradeInfoBlock — consolidated amber "Trade Info" cell.
 *
 * Yellow mirror of AutoPopBlock. Holds up to two trade-in units (Stock #,
 * Year, Make, Model, Miles per trade), auto-populated by the DealerBuilt
 * deal-number lookup; the operator can override any value by entering edit
 * mode. ACV/Retail live in their own columns, not here.
 */

import React, { useEffect, useRef, useState } from 'react';
import { NutSheetRowDraft } from '../../types/nutSheet';
import { numCell, textCell } from './cellStyles';
import { formatInt } from './formatters';

interface TradeInfoBlockProps {
  row: NutSheetRowDraft;
  onChange: (patch: Partial<NutSheetRowDraft>) => void;
  onCommit: () => void;
  saving: boolean;
}

// Read-view line: year make model + miles (stock # intentionally omitted to
// save space — it's still editable in the expanded view).
function tradeLine(
  year: number | null,
  make: string | null,
  model: string | null,
  miles: number | null,
): string {
  const head = [year ?? '', make ?? '', model ?? '']
    .filter((p) => p !== '' && p != null)
    .join(' ')
    .trim();
  const milesText = miles != null ? `${formatInt(miles)} mi` : '';
  return [head, milesText].filter(Boolean).join(' ');
}

export const TradeInfoBlock: React.FC<TradeInfoBlockProps> = ({
  row,
  onChange,
  onCommit,
  saving,
}) => {
  const [editing, setEditing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && firstInputRef.current) {
      firstInputRef.current.focus();
      firstInputRef.current.select();
    }
  }, [editing]);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget as Node | null)
    ) {
      setEditing(false);
      onCommit();
    }
  };

  const handleText =
    (field: keyof NutSheetRowDraft) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ [field]: e.target.value } as Partial<NutSheetRowDraft>);
    };

  const handleDigits =
    (field: keyof NutSheetRowDraft) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '');
      onChange({ [field]: digits === '' ? null : parseInt(digits, 10) } as Partial<NutSheetRowDraft>);
    };

  if (editing) {
    return (
      <div
        ref={containerRef}
        onBlur={handleBlur}
        className="bg-amber-100 border border-amber-400 rounded p-1.5 space-y-1 min-w-[240px]"
      >
        <div className="text-[10px] font-semibold text-amber-800">Trade 1</div>
        <div className="grid grid-cols-3 gap-1">
          <input
            ref={firstInputRef}
            type="text"
            placeholder="Stock #"
            value={row.tradeStockNumber ?? ''}
            onChange={handleText('tradeStockNumber')}
            className={textCell}
          />
          <input
            type="text"
            inputMode="numeric"
            placeholder="Year"
            value={row.tradeYear ?? ''}
            onChange={handleDigits('tradeYear')}
            className={numCell}
          />
          <input
            type="text"
            inputMode="numeric"
            placeholder="Miles"
            value={row.tradeMiles ?? ''}
            onChange={handleDigits('tradeMiles')}
            className={numCell}
          />
        </div>
        <div className="grid grid-cols-2 gap-1">
          <input
            type="text"
            placeholder="Make"
            value={row.tradeMake ?? ''}
            onChange={handleText('tradeMake')}
            className={textCell}
          />
          <input
            type="text"
            placeholder="Model"
            value={row.tradeModel ?? ''}
            onChange={handleText('tradeModel')}
            className={textCell}
          />
        </div>
        <input
          type="text"
          placeholder="Comment (e.g. Sending to Auction)"
          value={row.tradeComment ?? ''}
          onChange={handleText('tradeComment')}
          className={textCell}
        />

        <div className="text-[10px] font-semibold text-amber-800 pt-1">Trade 2</div>
        <div className="grid grid-cols-3 gap-1">
          <input
            type="text"
            placeholder="Stock #"
            value={row.trade2StockNumber ?? ''}
            onChange={handleText('trade2StockNumber')}
            className={textCell}
          />
          <input
            type="text"
            inputMode="numeric"
            placeholder="Year"
            value={row.trade2Year ?? ''}
            onChange={handleDigits('trade2Year')}
            className={numCell}
          />
          <input
            type="text"
            inputMode="numeric"
            placeholder="Miles"
            value={row.trade2Miles ?? ''}
            onChange={handleDigits('trade2Miles')}
            className={numCell}
          />
        </div>
        <div className="grid grid-cols-2 gap-1">
          <input
            type="text"
            placeholder="Make"
            value={row.trade2Make ?? ''}
            onChange={handleText('trade2Make')}
            className={textCell}
          />
          <input
            type="text"
            placeholder="Model"
            value={row.trade2Model ?? ''}
            onChange={handleText('trade2Model')}
            className={textCell}
          />
        </div>
        <input
          type="text"
          placeholder="Comment (e.g. Sending to Auction)"
          value={row.trade2Comment ?? ''}
          onChange={handleText('trade2Comment')}
          className={textCell}
        />
      </div>
    );
  }

  // Read view
  const line1 = tradeLine(row.tradeYear, row.tradeMake, row.tradeModel, row.tradeMiles);
  const line2 = tradeLine(row.trade2Year, row.trade2Make, row.trade2Model, row.trade2Miles);
  const comment1 = row.tradeComment?.trim() || '';
  const comment2 = row.trade2Comment?.trim() || '';

  return (
    <div
      ref={containerRef}
      onClick={() => !saving && setEditing(true)}
      className="bg-amber-100 border border-amber-400 rounded px-2 py-1 cursor-pointer hover:ring-1 hover:ring-amber-500 text-xs leading-tight min-w-[240px]"
      title="Click to edit"
    >
      {!line1 && !line2 && !comment1 && !comment2 ? (
        <span className="italic text-gray-400">No trade — enter stock # to auto-fill</span>
      ) : (
        <>
          {line1 && <div className="font-semibold text-gray-900">T1 {line1}</div>}
          {comment1 && <div className="italic text-amber-700">{comment1}</div>}
          {line2 && <div className="font-semibold text-gray-900">T2 {line2}</div>}
          {comment2 && <div className="italic text-amber-700">{comment2}</div>}
        </>
      )}
    </div>
  );
};
